import { useState, useEffect, useRef, useCallback } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { Loader2, PenLine, Eraser, CheckCircle2, ShieldCheck, FileText, AlertTriangle, FileDown } from "lucide-react";
import { Logo } from "../components/Logo";
import { formatBRL } from "../config";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full bg-[#0d0f17] border border-slate-800 focus:border-emerald-500 rounded-xl px-4 py-3 text-sm text-white placeholder:text-slate-500 outline-none transition-colors";

const CLAUSULAS = [
  "O veículo é entregue revisado e com o nível de combustível registrado em checklist, devendo ser devolvido nas mesmas condições.",
  "Diárias adicionais serão cobradas no mesmo valor da diária contratada, mediante aviso prévio à locadora.",
  "Multas de trânsito, danos ao veículo e despesas de mau uso no período são de responsabilidade do(a) locatário(a).",
  "A devolução fora do prazo sem aviso prévio gera cobrança de diárias adicionais e eventual taxa de indisponibilidade.",
  "É vedada a sublocação ou cessão do veículo a terceiros sem autorização por escrito da locadora.",
  "O pagamento do valor total é realizado no ato da retirada, salvo acordo diverso entre as partes.",
];

const SignaturePad = ({ onDraw }) => {
  const canvasRef = useRef(null);
  const drawing = useRef(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ratio = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * ratio;
    canvas.height = rect.height * ratio;
    const ctx = canvas.getContext("2d");
    ctx.scale(ratio, ratio);
    ctx.lineWidth = 2.4;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = "#0f172a";
  }, []);

  const pos = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };

  const start = (e) => {
    drawing.current = true;
    canvasRef.current.setPointerCapture(e.pointerId);
    const ctx = canvasRef.current.getContext("2d");
    const p = pos(e);
    ctx.beginPath();
    ctx.moveTo(p.x, p.y);
    onDraw();
  };

  const move = (e) => {
    if (!drawing.current) return;
    const ctx = canvasRef.current.getContext("2d");
    const p = pos(e);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
  };

  const end = () => { drawing.current = false; };

  return (
    <canvas
      ref={canvasRef}
      onPointerDown={start}
      onPointerMove={move}
      onPointerUp={end}
      onPointerLeave={end}
      className="w-full h-44 bg-white rounded-2xl touch-none cursor-crosshair"
      data-testid="signature-canvas"
    />
  );
};

export default function AssinarContrato() {
  const { rentalId } = useParams();
  const [data, setData] = useState(null);
  const [erro, setErro] = useState(null);
  const [nome, setNome] = useState("");
  const [cpf, setCpf] = useState("");
  const [aceite, setAceite] = useState(false);
  const [desenhou, setDesenhou] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [assinadoAgora, setAssinadoAgora] = useState(null);
  const canvasKey = useRef(0);

  useEffect(() => {
    axios.get(`${API}/rentals/${rentalId}/assinatura`)
      .then((res) => { setData(res.data); setNome(res.data.cliente || ""); })
      .catch((e) => setErro(e.response?.data?.detail || "Locação não encontrada."));
  }, [rentalId]);

  const limpar = () => {
    canvasKey.current += 1;
    setDesenhou(false);
  };

  const assinar = async () => {
    if (!aceite || !desenhou || nome.trim().length < 5) return;
    setErro(null);
    setEnviando(true);
    try {
      const canvas = document.querySelector('[data-testid="signature-canvas"]');
      const png = canvas.toDataURL("image/png");
      const res = await axios.post(`${API}/rentals/${rentalId}/assinatura`, {
        nome, cpf, aceite, assinatura_png: png,
      });
      setAssinadoAgora(res.data.assinado_em);
    } catch (e) {
      const d = e.response?.data?.detail;
      setErro(typeof d === "string" ? d : "Erro ao registrar assinatura.");
    } finally { setEnviando(false); }
  };

  const fmtData = (d) => d?.split("-").reverse().join("/");
  const fmtDataHora = (iso) => iso ? new Date(iso).toLocaleString("pt-BR", { timeZone: "America/Sao_Paulo" }) : "";

  return (
    <div className="min-h-screen bg-[#07090B] px-4 py-10" data-testid="assinatura-page">
      <div className="max-w-lg mx-auto space-y-6">
        <div className="flex justify-center"><a href="/"><Logo /></a></div>

        {erro && !data ? (
          <div className="glass rounded-3xl p-8 text-center space-y-3" data-testid="assinatura-erro">
            <AlertTriangle className="w-10 h-10 text-amber-400 mx-auto" />
            <p className="text-white font-semibold">{erro}</p>
            <p className="text-sm text-slate-400">Verifique o link recebido ou fale com a Seed Motors.</p>
          </div>
        ) : !data ? (
          <div className="h-40 rounded-3xl bg-[#131620] animate-pulse" />
        ) : (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
            <div className="text-center">
              <h1 className="font-display text-2xl font-bold text-white flex items-center justify-center gap-2">
                <FileText className="w-6 h-6 text-emerald-400" /> Contrato de locação
              </h1>
              <p className="text-sm text-slate-400 mt-1">Leia o resumo e assine com o dedo para confirmar a retirada.</p>
            </div>

            <div className="glass rounded-3xl p-6 space-y-3" data-testid="assinatura-resumo">
              <div className="flex justify-between text-sm"><span className="text-slate-400">Veículo</span><span className="text-white font-semibold text-right">{data.veiculo.nome} {data.veiculo.ano}</span></div>
              <div className="flex justify-between text-sm"><span className="text-slate-400">Locatário(a)</span><span className="text-white font-semibold">{data.cliente}</span></div>
              <div className="flex justify-between text-sm"><span className="text-slate-400">Período</span><span className="text-white">{fmtData(data.inicio)} a {fmtData(data.fim)}</span></div>
              <div className="flex justify-between text-sm"><span className="text-slate-400">Diárias</span><span className="text-white">{data.dias} × {formatBRL(data.diaria)}</span></div>
              <div className="border-t border-slate-800 pt-3 flex justify-between"><span className="text-slate-400 text-sm">Valor total</span><span className="font-display text-xl font-bold text-emerald-400">{formatBRL(data.valor_total)}</span></div>
            </div>

            <div className="glass rounded-3xl p-6" data-testid="assinatura-clausulas">
              <h2 className="font-display font-bold text-white mb-3 text-sm">Condições gerais</h2>
              <ol className="space-y-2 text-xs text-slate-400 list-decimal list-inside">
                {CLAUSULAS.map((c, i) => <li key={i}>{c}</li>)}
              </ol>
            </div>

            {data.assinada || assinadoAgora ? (
              <div className="glass rounded-3xl p-8 text-center space-y-3" data-testid="assinatura-sucesso">
                <CheckCircle2 className="w-12 h-12 text-emerald-400 mx-auto" />
                <h2 className="font-display text-xl font-bold text-white">Contrato assinado!</h2>
                <p className="text-sm text-slate-400">
                  Assinado digitalmente por <span className="text-white font-semibold">{data.assinatura?.nome || nome}</span>
                  {" "}em {fmtDataHora(data.assinatura?.assinado_em || assinadoAgora)}.
                </p>
                <p className="text-xs text-slate-500">Apresente esta tela na retirada do veículo. A assinatura já consta no contrato da Seed Motors.</p>
                <a
                  href={`${API}/rentals/${rentalId}/contrato.pdf`}
                  download
                  data-testid="assinatura-download-contrato"
                  className="inline-flex items-center justify-center gap-2 bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-bold px-6 py-3.5 rounded-full transition-colors text-sm"
                >
                  <FileDown className="w-4 h-4" /> Baixar contrato assinado (PDF)
                </a>
              </div>
            ) : (
              <div className="glass rounded-3xl p-6 space-y-4" data-testid="assinatura-form">
                <div>
                  <label className="text-xs text-slate-400 block mb-1.5">Nome completo*</label>
                  <input className={inputCls} value={nome} onChange={(e) => setNome(e.target.value)} data-testid="assinatura-nome" />
                </div>
                <div>
                  <label className="text-xs text-slate-400 block mb-1.5">CPF (opcional)</label>
                  <input className={inputCls} placeholder="000.000.000-00" value={cpf} onChange={(e) => setCpf(e.target.value)} data-testid="assinatura-cpf" />
                </div>
                <div>
                  <label className="text-xs text-slate-400 flex items-center gap-1.5 mb-2"><PenLine className="w-3.5 h-3.5 text-emerald-400" /> Desenhe sua assinatura*</label>
                  <SignaturePad key={canvasKey.current} onDraw={() => setDesenhou(true)} />
                  <div className="flex justify-end mt-2">
                    <button type="button" onClick={limpar} data-testid="assinatura-limpar" className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-400 hover:text-emerald-400 transition-colors">
                      <Eraser className="w-3.5 h-3.5" /> Limpar
                    </button>
                  </div>
                </div>
                <label className="flex items-start gap-3 text-xs text-slate-300 cursor-pointer" data-testid="assinatura-aceite-label">
                  <input type="checkbox" checked={aceite} onChange={(e) => setAceite(e.target.checked)} data-testid="assinatura-aceite" className="accent-emerald-500 w-4 h-4 mt-0.5 shrink-0" />
                  Li e concordo com as condições do contrato de locação da Seed Motors e confirmo que esta é a minha assinatura.
                </label>
                {erro && <p className="text-xs text-red-400" data-testid="assinatura-erro-msg">{erro}</p>}
                <button
                  onClick={assinar}
                  disabled={enviando || !aceite || !desenhou || nome.trim().length < 5}
                  data-testid="assinatura-confirmar"
                  className="w-full inline-flex items-center justify-center gap-2 bg-emerald-500 hover:bg-emerald-400 disabled:opacity-50 disabled:cursor-not-allowed text-emerald-950 font-bold px-6 py-4 rounded-full transition-colors"
                >
                  {enviando ? <Loader2 className="w-5 h-5 animate-spin" /> : <ShieldCheck className="w-5 h-5" />}
                  Assinar contrato
                </button>
              </div>
            )}

            <p className="text-center text-[11px] text-slate-600 pb-6">Seed Motors · Rua Santa Catarina, 122 B · Jardim Milenium · Araguari/MG</p>
          </motion.div>
        )}
      </div>
    </div>
  );
}
